import { type ReactNode, useCallback } from "react";
import { useNavigate } from "react-router";
import { Sheet } from "../../../shared/phone/Sheet";
import { Chevron } from "../../../shared/phone/Row";
import type { PartnerProgramMembership } from "../../../shared/types";
import { PARTNER_PORTAL } from "../../../shared/config";
import { usePartnerAuth } from "../../context/PartnerAuthContext";
import { initialsOf } from "../../lib/format";
import { Icon, type IconName } from "./Icon";
import { toast } from "./Toast";

/** "powered by Flashadz" at the foot of a native screen. */
export function PoweredBy() {
  const [lead, rest] = PARTNER_PORTAL.poweredBy.split("Flashadz");
  return (
    <p className="po-pow">
      {lead}
      <b>Flashadz</b>
      {rest}
    </p>
  );
}

/** A product's square: its initials on the accent. */
export function ProgramTile({ name, size = 32 }: { name: string; size?: number }) {
  const mono = initialsOf(name.split(/\s+/).length > 1 ? name : name.slice(0, 1));
  return (
    <span className="po-tile" style={{ width: size, height: size, fontSize: Math.round(size * (mono.length > 1 ? 0.36 : 0.44)) }} aria-hidden="true">
      {mono}
    </span>
  );
}

/** A person's round monogram. */
export function Monogram({ name }: { name: string }) {
  return (
    <span className="po-mono" aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

/** An honest empty state: an icon, what is missing, and what will fill it. */
export function EmptyBlock({ icon, title, children }: { icon: IconName; title: string; children: ReactNode }) {
  return (
    <div className="po-empty">
      <Icon name={icon} />
      <b>{title}</b>
      <p>{children}</p>
    </div>
  );
}

/** The dates beside a section head: a small pill inside a 44px target, opening a sheet of ranges. */
export function RangeButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="po-rng" onClick={onClick} aria-label={`Dates, ${label}`}>
      <span>
        <Icon name="cal" />
        {label}
        <Icon name="chevD" />
      </span>
    </button>
  );
}

/** A program's state in words, or null when it is simply active. */
function programState(p: PartnerProgramMembership): { label: string; tone: "warn" | "violet" } | null {
  if (p.needsResign) return { label: "Re-sign", tone: "warn" };
  if (!p.agreementSigned) return { label: "Sign to start", tone: "warn" };
  if (p.status === "pending") return { label: "Pending review", tone: "violet" };
  return null;
}

/**
 * Opening a program: one that is set up shows its Home; one that still needs its agreement signed (or
 * signed again) opens its setup.
 */
export function useOpenProgram(): (p: PartnerProgramMembership, replace?: boolean) => void {
  const { selectProgram, selectedProgramId } = usePartnerAuth();
  const navigate = useNavigate();
  return useCallback(
    (p: PartnerProgramMembership, replace = false) => {
      if (p.agreementSigned && !p.needsResign) {
        const switched = selectedProgramId !== null && p.programId !== selectedProgramId;
        selectProgram(p.programId);
        navigate("/home", { replace });
        if (switched) toast(`Showing ${p.productName}`);
      } else {
        navigate(`/onboarding/${p.programId}`, { replace });
      }
    },
    [navigate, selectProgram, selectedProgramId],
  );
}

/** One program as a row: its square, its name, its state, then a tick (the one showing) or a chevron. */
export function ProgramRow({ p, current, onOpen, showActive }: { p: PartnerProgramMembership; current: boolean; onOpen: () => void; showActive: boolean }) {
  const state = programState(p);
  const needsSetup = !p.agreementSigned || p.needsResign;
  const end = showActive || needsSetup ? <Chevron /> : current ? <Icon name="check" className="po-tick" /> : null;
  return (
    <button type="button" className="pn-row po-prog" onClick={onOpen}>
      <ProgramTile name={p.productName} size={36} />
      <span className="m">
        <span className="t">{p.productName}</span>
      </span>
      {state ? (
        <span className={`pn-pill ${state.tone}`}>{state.label}</span>
      ) : showActive ? (
        <span className="pn-pill good">
          <i />
          Active
        </span>
      ) : null}
      {end}
    </button>
  );
}

/** The partner's programs in a sheet, from Home's title: tap one to show it. */
export function ProgramsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { session, selectedProgramId } = usePartnerAuth();
  const openProgram = useOpenProgram();
  const programs = session?.programs ?? [];
  return (
    <Sheet open={open} onClose={onClose} title="Your programs">
      <div className="pn-grp">
        {programs.map((p) => (
          <ProgramRow
            key={p.programId}
            p={p}
            current={p.programId === selectedProgramId}
            showActive={false}
            onOpen={() => {
              onClose();
              if (p.programId !== selectedProgramId || !p.agreementSigned || p.needsResign) openProgram(p);
            }}
          />
        ))}
      </div>
    </Sheet>
  );
}
